import { useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { Button, Form, Alert, Card, ListGroup } from "react-bootstrap";

import { PersonPicker } from "./PersonPicker";
import { getCSRFToken } from "../utils/csrf";

const currentYear = new Date().getFullYear();

const asRow = (p) => ({
  perfil_id: p.id ?? p.perfil_id,
  nombre: p.nombre,
  apellido: p.apellido,
  DNI: p.DNI,
  email: p.email,
  telefono: p.telefono,
  domicilio: p.domicilio,
});

// Busca una persona ya existente para encontrar SU cuenta — a diferencia de
// PersonPicker, acá no tiene sentido ofrecer "crear nueva persona": buscar una
// cuenta existente solo funciona sobre gente que ya está en el sistema.
const AccountSearchBox = ({ buscarUrl, onPick }) => {
  const debounceRef = useRef(null);
  const loadOptions = (inputValue) =>
    new Promise((resolve) => {
      clearTimeout(debounceRef.current);
      const q = inputValue.trim();
      if (q.length < 2) {
        resolve([]);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        const res = await fetch(`${buscarUrl}?q=${encodeURIComponent(q)}`);
        resolve(await res.json());
      }, 300);
    });

  return (
    <AsyncSelect
      cacheOptions
      loadOptions={loadOptions}
      onChange={(o) => o && onPick(o)}
      getOptionValue={(o) => String(o.id)}
      getOptionLabel={(o) => o.label}
      placeholder="Buscar por nombre, apellido, DNI o email de cualquier integrante de la familia…"
      noOptionsMessage={() => "Escribí al menos 2 caracteres"}
      value={null}
    />
  );
};

const FieldRow = ({ row, onChange }) => (
  <div className="row g-2 mb-2">
    <div className="col-md-3">
      <Form.Control placeholder="Nombre" value={row.nombre || ""} onChange={(e) => onChange({ nombre: e.target.value })} />
    </div>
    <div className="col-md-3">
      <Form.Control placeholder="Apellido" value={row.apellido || ""} onChange={(e) => onChange({ apellido: e.target.value })} />
    </div>
    <div className="col-md-2">
      <Form.Control placeholder="DNI" value={row.DNI || ""} onChange={(e) => onChange({ DNI: e.target.value })} />
    </div>
    <div className="col-md-2">
      <Form.Control placeholder="Teléfono" value={row.telefono || ""} onChange={(e) => onChange({ telefono: e.target.value })} />
    </div>
    <div className="col-md-2">
      <Form.Control placeholder="Domicilio" value={row.domicilio || ""} onChange={(e) => onChange({ domicilio: e.target.value })} />
    </div>
    <div className="col-md-6">
      <Form.Control type="email" placeholder="Email" value={row.email || ""} onChange={(e) => onChange({ email: e.target.value })} />
    </div>
  </div>
);

const CUENTA_META_VACIA = { descripcion: "", doc_tipo: "", doc_nro: "", activo: true };

export const AccountEditor = ({ urls, cursoOptions, servicioOptions, docTipoOptions }) => {
  const [account, setAccount] = useState(null);
  const [cuentaMeta, setCuentaMeta] = useState(CUENTA_META_VACIA);
  const [contactoIndex, setContactoIndex] = useState(0);
  const [cuentaChoices, setCuentaChoices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addingParent, setAddingParent] = useState(false);
  const [addingChild, setAddingChild] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const cargarCuenta = async (cuentaId) => {
    setLoading(true);
    setCuentaChoices(null);
    setErrors({});
    setSaved(false);
    const res = await fetch(urls.cuenta.replace("999999999", cuentaId));
    const data = await res.json();
    setCuentaMeta({ ...CUENTA_META_VACIA, ...data.cuenta });
    setAccount({
      cuenta_id: data.cuenta_id,
      parents: data.parents.map(asRow),
      children: data.children.map((c) => ({
        ...asRow(c),
        curso_id: c.curso_id,
        anio: c.anio || currentYear,
        division: c.division || "A",
        servicios: c.contratos.map((ct) => ct.servicio_id),
      })),
    });
    setContactoIndex(Math.max(0, data.parents.findIndex((p) => p.id === data.contacto_perfil_id)));
    setLoading(false);
  };

  const buscarCuentasDePersona = async (persona) => {
    setLoading(true);
    setErrors({});
    const res = await fetch(urls.personaCuentas.replace("999999999", persona.id));
    const cuentas = await res.json();
    setLoading(false);
    if (cuentas.length === 0) {
      setErrors({ non_field: `${persona.label} no está asociado/a a ninguna cuenta todavía.` });
    } else if (cuentas.length === 1) {
      cargarCuenta(cuentas[0].id);
    } else {
      setCuentaChoices(cuentas);
    }
  };

  const updateParent = (idx, patch) =>
    setAccount((a) => ({ ...a, parents: a.parents.map((p, i) => (i === idx ? { ...p, ...patch } : p)) }));
  const updateChild = (idx, patch) =>
    setAccount((a) => ({ ...a, children: a.children.map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));

  const addParent = (picked) => {
    setAccount((a) => ({ ...a, parents: [...a.parents, asRow(picked)] }));
    setAddingParent(false);
  };
  const addChild = (picked) => {
    setAccount((a) => ({
      ...a,
      children: [...a.children, { ...asRow(picked), curso_id: null, anio: currentYear, division: "A", servicios: [] }],
    }));
    setAddingChild(false);
  };

  const handleGuardar = async () => {
    setSaving(true);
    setErrors({});
    const payload = {
      contacto_index: contactoIndex,
      cuenta: cuentaMeta,
      parents: account.parents,
      children: account.children,
    };
    try {
      const res = await fetch(urls.cuentaGuardar.replace("999999999", account.cuenta_id), {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRFToken": getCSRFToken() },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.ok) {
        setSaved(true);
        cargarCuenta(account.cuenta_id);
      } else {
        setErrors(data.errors || {});
      }
    } catch (e) {
      setErrors({ non_field: "Error de red — intentá de nuevo." });
    } finally {
      setSaving(false);
    }
  };

  if (!account) {
    return (
      <div>
        <AccountSearchBox buscarUrl={urls.buscarPersona} onPick={buscarCuentasDePersona} />
        {loading && <div className="mt-2 text-muted">Buscando…</div>}
        {errors.non_field && (
          <Alert variant="warning" className="mt-3">
            {errors.non_field}
          </Alert>
        )}
        {cuentaChoices && (
          <ListGroup className="mt-3">
            {cuentaChoices.map((c) => (
              <ListGroup.Item key={c.id} action onClick={() => cargarCuenta(c.id)}>
                {c.label}
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </div>
    );
  }

  return (
    <div>
      <Button variant="link" className="ps-0 mb-3" onClick={() => setAccount(null)}>
        ← Buscar otra cuenta
      </Button>

      {errors.non_field && <Alert variant="danger">{errors.non_field}</Alert>}
      {saved && !Object.keys(errors).length && <Alert variant="success">Cambios guardados.</Alert>}

      <h5>Datos de la cuenta</h5>
      <Card className="mb-4">
        <Card.Body>
          <div className="row g-2">
            <div className="col-md-4">
              <Form.Label className="small mb-1">Descripción</Form.Label>
              <Form.Control
                placeholder={`Cuenta #${account.cuenta_id}`}
                value={cuentaMeta.descripcion}
                onChange={(e) => setCuentaMeta({ ...cuentaMeta, descripcion: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <Form.Label className="small mb-1">Tipo de documento</Form.Label>
              <Form.Select
                value={cuentaMeta.doc_tipo || ""}
                onChange={(e) => setCuentaMeta({ ...cuentaMeta, doc_tipo: e.target.value })}
              >
                <option value="">—</option>
                {docTipoOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-3">
              <Form.Label className="small mb-1">N° de documento</Form.Label>
              <Form.Control
                value={cuentaMeta.doc_nro}
                onChange={(e) => setCuentaMeta({ ...cuentaMeta, doc_nro: e.target.value })}
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <Form.Check
                type="checkbox"
                label="Activa"
                checked={cuentaMeta.activo}
                onChange={(e) => setCuentaMeta({ ...cuentaMeta, activo: e.target.checked })}
              />
            </div>
          </div>
          {errors.cuenta && <div className="text-danger small mt-2">{errors.cuenta}</div>}
        </Card.Body>
      </Card>

      <h5>Padres / madres</h5>
      {account.parents.map((p, idx) => (
        <Card className="mb-2" key={p.perfil_id ?? `nuevo-${idx}`}>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Check
                type="radio"
                name="contacto"
                label="Responsable de facturación"
                checked={contactoIndex === idx}
                onChange={() => setContactoIndex(idx)}
              />
              {!p.perfil_id && <span className="badge text-bg-secondary">nueva persona</span>}
            </div>
            <FieldRow row={p} onChange={(patch) => updateParent(idx, patch)} />
            {errors[`parent${idx}`] && <div className="text-danger small">{errors[`parent${idx}`]}</div>}
          </Card.Body>
        </Card>
      ))}
      {addingParent ? (
        <PersonPicker value={null} onChange={(v) => v && addParent(v)} buscarUrl={urls.buscarPersona} />
      ) : (
        <Button variant="link" className="ps-0" onClick={() => setAddingParent(true)}>
          + Agregar padre/madre
        </Button>
      )}

      <h5 className="mt-4">Hijos / as</h5>
      {account.children.map((c, idx) => (
        <Card className="mb-2" key={c.perfil_id ?? `nuevo-${idx}`}>
          <Card.Body>
            {!c.perfil_id && <span className="badge text-bg-secondary mb-2">nueva persona</span>}
            <FieldRow row={c} onChange={(patch) => updateChild(idx, patch)} />
            <div className="row g-2 mb-2">
              <div className="col-md-5">
                <Select
                  options={cursoOptions}
                  value={cursoOptions.find((o) => o.value === c.curso_id) || null}
                  onChange={(o) => updateChild(idx, { curso_id: o ? o.value : null })}
                  placeholder="Curso…"
                />
              </div>
              <div className="col-md-3">
                <Form.Control
                  type="number"
                  placeholder="Año"
                  value={c.anio || ""}
                  onChange={(e) => updateChild(idx, { anio: parseInt(e.target.value, 10) || "" })}
                />
              </div>
              <div className="col-md-4">
                <Select
                  isMulti
                  options={servicioOptions}
                  value={servicioOptions.filter((o) => c.servicios.includes(o.value))}
                  onChange={(opts) => updateChild(idx, { servicios: (opts || []).map((o) => o.value) })}
                  placeholder="Servicios…"
                />
              </div>
            </div>
            {errors[`child${idx}`] && <div className="text-danger small">{errors[`child${idx}`]}</div>}
            {Object.entries(errors)
              .filter(([k]) => c.perfil_id && k.startsWith(`child_${c.perfil_id}_`))
              .map(([k, v]) => (
                <div className="text-danger small" key={k}>
                  {v}
                </div>
              ))}
          </Card.Body>
        </Card>
      ))}
      {addingChild ? (
        <PersonPicker value={null} onChange={(v) => v && addChild(v)} buscarUrl={urls.buscarPersona} showFecNac />
      ) : (
        <Button variant="link" className="ps-0" onClick={() => setAddingChild(true)}>
          + Agregar hijo/a
        </Button>
      )}

      <div className="mt-4">
        <Button variant="primary" disabled={saving} onClick={handleGuardar}>
          {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
      </div>
    </div>
  );
};

export default AccountEditor;
